/**
 * Gozzer Ranch Golf & Lake Club — Harrison, Idaho.
 *
 * Tom Fazio, 2007, private, on the bluff above Lake Coeur d'Alene. The
 * tournament's home course in 2024 and 2025.
 *
 * Transcribed 2026-07-29 from public scorecard databases, NOT from the physical
 * card. Nothing here has been matched against the card in the pro shop, so this
 * ships unverified like everything else in `seed.ts`.
 *
 * Note on tee ids: Gozzer's tees are numbered (I, II, III, IV), not colored.
 * The ids below are deliberately the four `YARD_TEES` slot keys from `seed.ts`
 * — black/green/gold/bronze, longest to shortest — so that `cardHoles` lands
 * each yardage row on the tee it actually belongs to. The club's real tee names
 * live in `name`. The swatch colors that `tees()` derives from those ids are
 * meaningless for this course; the yardages are not.
 */

import { tees, type Seed } from '../card'

export const GOZZER_RANCH: Seed = {
  id: 'gozzer-ranch',
  name: 'Gozzer Ranch',
  par: 72,
  pars: [4, 5, 3, 4, 4, 4, 3, 5, 4, 5, 4, 4, 4, 3, 4, 5, 3, 4],
  si: [9, 11, 13, 17, 5, 1, 15, 7, 3, 12, 4, 14, 2, 18, 8, 16, 10, 6],
  yards: [
    [461, 589, 196, 287, 484, 446, 206, 601, 465, 581, 481, 323, 487, 186, 456, 501, 181, 495],
    [404, 543, 169, 271, 423, 396, 200, 575, 425, 516, 447, 355, 438, 156, 407, 495, 167, 421],
    [375, 495, 162, 263, 404, 374, 176, 529, 414, 488, 416, 315, 415, 145, 358, 485, 155, 377],
    [338, 475, 147, 251, 377, 365, 162, 483, 385, 462, 380, 277, 387, 105, 330, 439, 138, 350],
  ],
  tees: tees([
    ['black', 'Tee I', 75.0, 144, 7426],
    ['green', 'Tee II', 72.6, 137, 6808],
    ['gold', 'Tee III', 70.8, 127, 6346],
    ['bronze', 'Tee IV', 68.4, 122, 5851],
  ]),
  provenance:
    'Par is the safest number here: five independent databases (golfify, ' +
    '18birdies, hole19, allsquaregolf, golfpass) print the same 36 out / 36 in, ' +
    'four par 5s and four par 3s, for 72. Stroke index is taken from golfify, ' +
    'which agrees hole-for-hole with 18birdies and hole19; allsquaregolf matches ' +
    'on sixteen of eighteen and is provably corrupt on the other two, printing SI ' +
    '11 on both hole 2 and hole 6 and SI 5 on both hole 5 and hole 7 while never ' +
    'using 1 or 15. There is one real dissent, and it is not small: golfpass and ' +
    'golfweatherscore publish a DIFFERENT and also internally valid stroke index ' +
    'that makes hole 5 the number 1 instead of hole 6, and reshuffles 1, 8, 9, ' +
    '10, 11, 12, 14, 16 and 18 as well. Golfpass is discounted because its own ' +
    'hole table contradicts its own tee table — its Tee I column sums to 6,939 ' +
    'against the 7,317 it prints at the top — but if the card in the pro shop ' +
    'says hole 5 is stroke 1, then this si array is the wrong family altogether ' +
    'and wants replacing rather than patching. Yardages are golfify: all four ' +
    'columns sum exactly to their printed totals, and 6,808 / 6,346 / 5,851 for ' +
    'Tees II-IV are corroborated by 18birdies, golfpass and bluegolf, which is ' +
    'the strongest arithmetic check available on this course. PHOTOGRAPH HOLE 12 ' +
    'FIRST: Tee I plays 323 there while Tee II plays 355, so the tips are shorter ' +
    'than the members’ tee on that one hole. Golfify and 18birdies both print ' +
    'it that way and both inward nines still total correctly with it, so it is ' +
    'probably a genuinely angled back box on a short par 4 — but it is also ' +
    'exactly what a swapped cell looks like, and it will trip any monotonic-' +
    'yardage check. Tee I’s total is contested too: 7,426 at 75.0/144 here and ' +
    'on 18birdies, foretee and teeoff, against 7,317 at 74.7/139 on bluegolf and ' +
    '7,143 on golflink — that spread moves every course handicap, so read the ' +
    'plate on the first tee. Tee V (5,100) is omitted because its men’s rating ' +
    'is published as both 68.2/117 and 69.8/128 and neither is credible for the ' +
    'length.',
}
